import React from 'react';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { SeverityPill } from '../../../components/Redesing/severity-pill';
import CardLoader from '../../../components/Redesing/card-loader';
import { UnlockEventsTable } from '../../../components/Unlocks/UnlockEventsTable';
import {
  countFailedUnlockAttempts,
  IUnlockRow,
  UNLOCK_SUCCESS_BACKWARD_WINDOW_SECONDS,
  UNLOCK_SUCCESS_WINDOW_SECONDS,
} from '../../../util/unlockEvents';
import { HARDWARE_UNLOCKS_WINDOW_DAYS } from '../../../hooks/hardware/query/V2/useHardwareUnlocksRelay';

function HardwareUnlocks({
  rows,
  successRate,
  isLoading,
  error,
}: {
  rows: IUnlockRow[];
  successRate: number | null;
  isLoading: boolean;
  error: Error | null;
}) {
  if (isLoading) {
    return <CardLoader useCard dataId="hardware-unlocks-loader" />;
  }

  if (error) {
    return (
      <Card>
        <Typography color="text.secondary" sx={{ p: 3 }}>
          Unlock events could not be loaded for this hardware.
        </Typography>
      </Card>
    );
  }

  const attemptCount = rows.length;
  const failedCount = countFailedUnlockAttempts(rows);
  const succeededCount = attemptCount - failedCount;

  return (
    <Card data-id="hardware-unlocks">
      <CardHeader
        title="Unlock Events"
        subheader={`Last ${HARDWARE_UNLOCKS_WINDOW_DAYS} days`}
        action={
          <Stack direction="row" spacing={1} sx={{ pt: 1, pr: 1 }}>
            {attemptCount === 0 && <SeverityPill color="info">No attempts</SeverityPill>}
            {succeededCount > 0 && (
              <SeverityPill color="success">{succeededCount} successful</SeverityPill>
            )}
            {failedCount > 0 && (
              <SeverityPill color="error">{failedCount} failed</SeverityPill>
            )}
          </Stack>
        }
      />
      <Stack spacing={0.5} sx={{ px: 3, pb: 2 }}>
        <Typography variant="h4" data-id="hardware-unlock-success-rate">
          {successRate !== null ? `${Number(successRate.toFixed(1))}%` : '—'}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Unlock success rate over the last {HARDWARE_UNLOCKS_WINDOW_DAYS} days. An
          attempt is marked failed when no unlock success was recorded from{' '}
          {UNLOCK_SUCCESS_BACKWARD_WINDOW_SECONDS} seconds before the attempt to{' '}
          {UNLOCK_SUCCESS_WINDOW_SECONDS} seconds after.
        </Typography>
      </Stack>
      {rows.length === 0 ? (
        <Typography color="text.secondary" sx={{ px: 3, pb: 3 }}>
          No unlock attempts were recorded in the last {HARDWARE_UNLOCKS_WINDOW_DAYS}{' '}
          days.
        </Typography>
      ) : (
        <UnlockEventsTable rows={rows} />
      )}
    </Card>
  );
}

export default HardwareUnlocks;
